import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { coreApi } from '@/lib/coreApi'

interface PoolNominee {
  id: string
  type: 'teacher' | 'prospect'
  name: string
  detail: string | null
  weight: number
  support_code: string | null
  status: string
}

interface PoolData {
  my_prospects: PoolNominee[]
  my_nomination: PoolNominee | null
}

export default function NomineeMyLink() {
  const { profile } = useAuth()
  const role = (profile?.role || '').toLowerCase()
  const isCoach = role === 'coach' || role === 'admin' || role === 'superadmin'
  const [data, setData] = useState<PoolData | null>(null)
  const [loading, setLoading] = useState(true)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  useEffect(() => {
    coreApi<{ data: PoolData }>('/api/v1/nominations/pool')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const copy = (code: string) => {
    const url = `${window.location.origin}/support/${code}`
    navigator.clipboard.writeText(url)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const share = async (name: string, code: string) => {
    const url = `${window.location.origin}/support/${code}`
    const text = `Support ${name} on Project Tower! Every vote increases their weight in the draw.`
    if ((navigator as any).share) {
      try {
        await (navigator as any).share({ title: `Support ${name}`, text, url })
      } catch { /* user canceled */ }
    } else {
      navigator.clipboard.writeText(`${text} ${url}`)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-cocoa border-t-transparent" />
      </div>
    )
  }

  // Build list of links this user controls
  const myLinks: { name: string; code: string; weight: number; detail: string | null; type: string; status: string }[] = []
  if (data?.my_nomination?.support_code) {
    myLinks.push({
      name: data.my_nomination.name,
      code: data.my_nomination.support_code,
      weight: data.my_nomination.weight,
      detail: data.my_nomination.detail,
      type: data.my_nomination.type,
      status: data.my_nomination.status,
    })
  }
  if (isCoach && data?.my_prospects) {
    for (const p of data.my_prospects) {
      if (p.support_code) {
        myLinks.push({
          name: p.name,
          code: p.support_code,
          weight: p.weight,
          detail: p.detail,
          type: p.type,
          status: p.status,
        })
      }
    }
  }

  if (myLinks.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-sand bg-white/70 p-8 text-center">
        <h2 className="text-lg font-bold text-cocoa">No support links yet</h2>
        <p className="mt-2 text-sm text-cocoa/60">
          Your support link appears here once your nomination is confirmed.
        </p>
        {!isCoach && (
          <Link to="/nominate" className="mt-4 inline-block text-sm text-cocoa underline">Nominate someone</Link>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-bold text-cocoa">Your Support Links</h2>
        <p className="text-xs text-cocoa/60">
          Share these links to rally community support. Every vote adds +1 weight to the nominee's odds in the draw.
        </p>
      </header>

      <div className="space-y-4">
        {myLinks.map((link) => {
          const url = `${window.location.origin}/support/${link.code}`
          const isCopied = copiedCode === link.code
          return (
            <div key={link.code} className="rounded-2xl bg-white/80 p-6 shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-cocoa">{link.name}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      link.type === 'teacher' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {link.type}
                    </span>
                  </div>
                  {link.detail && <p className="text-xs text-cocoa/50">{link.detail}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-cocoa">{link.weight}</p>
                  <p className="text-[10px] text-cocoa/40">current weight</p>
                </div>
              </div>

              <div className="rounded-xl bg-sand/30 p-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-cocoa/50">Shareable link</p>
                <code className="block break-all text-sm font-mono text-cocoa">{url}</code>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => copy(link.code)}
                  className="rounded-xl bg-cocoa px-5 py-2.5 text-sm font-bold text-white transition hover:bg-cocoa/90"
                >
                  {isCopied ? '✓ Copied!' : '📋 Copy Link'}
                </button>
                <button
                  type="button"
                  onClick={() => share(link.name, link.code)}
                  className="rounded-xl border border-cocoa/20 px-5 py-2.5 text-sm font-semibold text-cocoa transition hover:bg-sand/30"
                >
                  📤 Share
                </button>
                <a
                  href={`mailto:?subject=${encodeURIComponent(`Support ${link.name} on Project Tower`)}&body=${encodeURIComponent(`Every vote increases their weight in the draw.\n\n${url}`)}`}
                  className="rounded-xl border border-cocoa/20 px-5 py-2.5 text-sm font-semibold text-cocoa transition hover:bg-sand/30"
                >
                  ✉️ Email
                </a>
                <a
                  href={`sms:?&body=${encodeURIComponent(`Support ${link.name} on Project Tower! ${url}`)}`}
                  className="rounded-xl border border-cocoa/20 px-5 py-2.5 text-sm font-semibold text-cocoa transition hover:bg-sand/30"
                >
                  💬 Text
                </a>
                <Link
                  to={`/support/${link.code}`}
                  target="_blank"
                  className="rounded-xl border border-cocoa/20 px-5 py-2.5 text-sm font-semibold text-cocoa transition hover:bg-sand/30"
                >
                  👁️ Preview
                </Link>
              </div>

              <div className="rounded-xl bg-stone-50 p-3 text-xs text-cocoa/70">
                <strong>Tip:</strong> Share in group chats, social media, and email. You can also post a pin on the{' '}
                <Link to="/portal/nominee/rally" className="underline hover:text-cocoa">Rally Board</Link> with a short message.
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
